import { createContext, useContext, useMemo, type ReactNode } from "react";

// Tool calls address knowledge bases and agents by UUID only. Their names ride
// the call's own backend metadata (KBToolMetadata.kb_name /
// AgentToolMetadata.agent_name) — no client-side entity fetch. The context
// carries the per-call lookup down to the approval render sites.
export interface EntityNameLookup {
  kbNameFor(id: string | null | undefined): string | null;
  agentNameFor(id: string | null | undefined): string | null;
}

const EMPTY_LOOKUP: EntityNameLookup = {
  kbNameFor: () => null,
  agentNameFor: () => null,
};

const EntityNamesContext = createContext<EntityNameLookup>(EMPTY_LOOKUP);

export function useEntityNameLookup(): EntityNameLookup {
  return useContext(EntityNamesContext);
}

type PartLike = {
  callProviderMetadata?: { noxus?: Record<string, unknown> | null } | null;
};

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// Build the id→name lookup from a single tool call's noxus metadata. The name
// only resolves for the entity the call actually names (its kb_id / agent_id);
// any other id falls through to null, so the caller shows the raw value.
export function lookupFromPart(part: PartLike | undefined): EntityNameLookup {
  const meta = part?.callProviderMetadata?.noxus;
  const kbId = str(meta?.kb_id);
  const kbName = str(meta?.kb_name);
  const agentId = str(meta?.agent_id);
  const agentName = str(meta?.agent_name);
  return {
    kbNameFor: (id) => (id && id === kbId ? (kbName ?? null) : null),
    agentNameFor: (id) => (id && id === agentId ? (agentName ?? null) : null),
  };
}

export function EntityNamesProvider({
  part,
  children,
}: {
  part: PartLike | undefined;
  children: ReactNode;
}) {
  const lookup = useMemo(() => lookupFromPart(part), [part]);
  return (
    <EntityNamesContext.Provider value={lookup}>
      {children}
    </EntityNamesContext.Provider>
  );
}
